/**
 * Chip primitive - Direction A "Tu phap restraint"
 *
 * 5 variant: default | active | success | warning | danger
 * 2 size: sm (24px) | md (32px)
 * Optional removable: hien nut x va goi onRemove.
 *
 * A11y:
 *  - aria-pressed cho toggle chip (caller pass)
 *  - role=button khi co onClick or removable
 *  - remove button co aria-label rieng "Xoa <text>"
 *
 * Phase 1 Track 2 (2026-05-22).
 */

import * as React from 'react';

export type ChipVariant =
  | 'default'
  | 'active'
  | 'success'
  | 'warning'
  | 'danger';
export type ChipSize = 'sm' | 'md';

export interface ChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  variant?: ChipVariant;
  size?: ChipSize;
  removable?: boolean;
  onRemove?: () => void;
  /** Aria-pressed cho toggle filter chip. */
  'aria-pressed'?: boolean;
  children: React.ReactNode;
}

export const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  {
    variant = 'default',
    size = 'md',
    removable = false,
    onRemove,
    className,
    onClick,
    children,
    'aria-pressed': ariaPressed,
    ...rest
  },
  ref
) {
  const cls = [
    'ask-chip',
    `ask-chip--${variant}`,
    `ask-chip--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const interactive = Boolean(onClick) || removable;
  const roleAttr =
    interactive && ariaPressed === undefined ? 'button' : undefined;

  return (
    <span
      ref={ref}
      className={cls}
      role={roleAttr}
      aria-pressed={ariaPressed}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent<HTMLSpanElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(e as unknown as React.MouseEvent<HTMLSpanElement>);
              }
            }
          : undefined
      }
      {...rest}
    >
      <span>{children}</span>
      {removable ? (
        <button
          type="button"
          className="ask-chip__remove"
          aria-label={
            typeof children === 'string' ? `Xoa ${children}` : 'Xoa muc'
          }
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (onRemove) onRemove();
          }}
        >
          <span aria-hidden="true">x</span>
        </button>
      ) : null}
    </span>
  );
});
